import styled, { createGlobalStyle, css } from 'styled-components';
import { alpha } from '@mui/material/styles';

// Shared patterns
const fontFamily = (props) => props.theme?.typography?.body1?.fontFamily || '"Poppins", "Arial", sans-serif';

const listReset = css`
  list-style: none;
  margin: 0;
  padding: 0;
`;

/** Stronger blush wash than accent.light (0.14) so header action hovers read clearly. */
const headerActionInteractive = css`
  transition: background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease,
    transform 0.18s ease;

  &:hover {
    background-color: ${(props) => alpha(props.theme.palette.accent.main, 0.2)};
    color: ${(props) => props.theme.palette.applied?.dark || props.theme.palette.accent.main};
  }

  &:active {
    background-color: ${(props) => alpha(props.theme.palette.accent.main, 0.28)};
    color: ${(props) => props.theme.palette.applied?.dark || props.theme.palette.accent.main};
    transform: scale(0.96);
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.palette.accent.main};
    outline-offset: 2px;
  }
`;

// Global Header Offset
// When the mobile bottom tab bar is showing (≤800px), the site header is hidden
// and body padding is cleared. On listing detail routes, header hide aligns with
// floating hero chrome (≤750px / below influencerSidebar) so 751–800px keeps chrome.
/** Shared height of the dashboard navbar and the header's top row. */
export const SITE_CHROME_BAR_HEIGHT_PX = 56;
/** Icon buttons and avatars inside that bar. */
export const SITE_CHROME_CONTROL_SIZE_PX = 32;
/** Content height of MobileBrandBar excluding safe-area (8 + 32 logo + 8 gap + 44 search + 12). */
export const MOBILE_BRAND_BAR_CONTENT_HEIGHT_PX = 104;
/** Guest mobile bar: logo row only (8 + 32 + 12). */
export const MOBILE_BRAND_BAR_CONTENT_HEIGHT_COMPACT_PX = 52;
const SITE_HEADER_INSET_PX = 12;
const SITE_HEADER_PAD_Y_PX = 8;
const SITE_HEADER_CATEGORY_ROW_PX = 36;
const SITE_HEADER_BORDER_PX = 1;
/** Top band includes the padding above the chrome bar so its controls sit on the band's midpoint. */
const SITE_HEADER_MAIN_BAND_PX = SITE_CHROME_BAR_HEIGHT_PX + SITE_HEADER_PAD_Y_PX;
/** Curved facet band includes the padding below the chips so they sit on that band's midpoint. */
const SITE_HEADER_CATEGORY_BAND_PX = SITE_HEADER_CATEGORY_ROW_PX + SITE_HEADER_PAD_Y_PX;
export const MARKETING_MAX_WIDTH = 1480;
/** Bottom-corner radius for the flush-top header / mobile brand bar. */
export const SITE_HEADER_CORNER_RADIUS_PX = 8;
/** Header card: top band + curved facet band + bottom border. */
export const SITE_HEADER_INNER_HEIGHT_PX = SITE_HEADER_MAIN_BAND_PX
  + SITE_HEADER_CATEGORY_BAND_PX
  + SITE_HEADER_BORDER_PX;
/** Reserved document offset: card + gap below. */
export const SITE_HEADER_CONTENT_HEIGHT_PX = SITE_HEADER_INNER_HEIGHT_PX + SITE_HEADER_INSET_PX;
/** Mobile header has no category chips and stays flush (bottom nav owns chrome). */
export const SITE_HEADER_CONTENT_HEIGHT_MOBILE_PX = (SITE_HEADER_PAD_Y_PX * 2)
  + SITE_CHROME_BAR_HEIGHT_PX
  + SITE_HEADER_BORDER_PX;

export const HeaderOffset = createGlobalStyle`
  body {
    padding-top: ${SITE_HEADER_CONTENT_HEIGHT_PX}px;
    @media (max-width: 768px) { padding-top: ${SITE_HEADER_CONTENT_HEIGHT_MOBILE_PX}px; }
    @media (max-width: 800px) {
      ${({ $hideForMobileBottomNav }) => $hideForMobileBottomNav && css`
        padding-top: 0;
      `}

      &.has-mobile-brand-bar {
        padding-top: calc(${MOBILE_BRAND_BAR_CONTENT_HEIGHT_PX}px + env(safe-area-inset-top, 0px));
      }

      &.has-mobile-brand-bar-compact {
        padding-top: calc(${MOBILE_BRAND_BAR_CONTENT_HEIGHT_COMPACT_PX}px + env(safe-area-inset-top, 0px));
      }
    }
  }
`;

export const HeaderWrapper = styled.header`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 0;
  padding: 0 20px;
  background-color: ${(props) => props.theme.palette.background.default};
  color: ${(props) => props.theme.palette.text.primary};
  font-family: ${fontFamily};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10010;
  height: ${SITE_HEADER_INNER_HEIGHT_PX}px;
  width: min(${MARKETING_MAX_WIDTH}px, calc(100% - ${SITE_HEADER_INSET_PX * 2}px));
  margin-left: auto;
  margin-right: auto;
  overflow: visible;
  box-sizing: border-box;
  border-radius: 0 0 ${SITE_HEADER_CORNER_RADIUS_PX}px ${SITE_HEADER_CORNER_RADIUS_PX}px;
  border: 1px solid ${(props) => props.theme.palette.border?.subtle};
  border-top: none;
  body.bottom-nav-menu-open & { display: none; }

  /* Hidden on mobile when bottom nav or listing-detail hero chrome owns the top of the screen */
  ${({ $hideForMobileBottomNav }) => $hideForMobileBottomNav && css`
    display: none;
  `}

  @media (max-width: 800px) {
    left: 0;
    right: 0;
    width: 100%;
    margin-left: 0;
    margin-right: 0;
    padding: 8px 15px;
    height: ${SITE_HEADER_CONTENT_HEIGHT_MOBILE_PX}px;
    gap: 0;
    justify-content: center;
  }
`;

export const HeaderMainRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 0;
  height: ${SITE_HEADER_MAIN_BAND_PX}px;
  min-height: ${SITE_HEADER_MAIN_BAND_PX}px;
  flex-shrink: 0;
  width: calc(100% + 40px);
  margin-left: -20px;
  margin-right: -20px;
  padding: 0 20px;
  box-sizing: border-box;
  border-bottom: 1px solid ${(props) => props.theme.palette.border?.subtle};

  @media (max-width: 800px) {
    width: 100%;
    margin: 0;
    padding: 0;
    height: ${SITE_CHROME_BAR_HEIGHT_PX}px;
    min-height: ${SITE_CHROME_BAR_HEIGHT_PX}px;
    border-bottom: none;
  }
`;

export const HeaderCategoryRow = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  flex: 0 0 ${SITE_HEADER_CATEGORY_BAND_PX}px;
  min-width: 0;
  height: ${SITE_HEADER_CATEGORY_BAND_PX}px;
  min-height: ${SITE_HEADER_CATEGORY_BAND_PX}px;
  width: 100%;
  padding: 0;
  box-sizing: border-box;
  overflow: visible;

  @media (max-width: 800px) {
    display: none;
  }
`;

// Logo
export const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 0;
  z-index: 2;

  .logo-wordmark {
    display: inline-flex;
    font-size: var(--font-title);
  }
`;

export const LogoLink = styled.a`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  line-height: 0;
`;

/** Guest marketplace links sit beside the wordmark on the top-left. */
export const HeaderGuestNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 12px;
  min-width: 0;
`;

export const HeaderGuestLink = styled.a`
  text-decoration: none;
  color: ${(props) => props.theme.palette.text.secondary};
  font-weight: 500;
  font-family: ${fontFamily};
  font-size: var(--font-body-sm);
  position: relative;
  padding: 6px 10px;
  transition: color 0.16s ease, background-color 0.16s ease;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  border-radius: 999px;
  cursor: pointer;
  line-height: 1.2;

  &:hover {
    color: ${(props) => props.theme.palette.text.primary};
    background-color: ${(props) => props.theme.palette.applied?.light};
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.palette.accent.main};
    outline-offset: 2px;
  }
`;

/** Inline-size container on HeaderMarketplaceSearch for pill/category scaling. */
export const HEADER_SEARCH_CONTAINER_NAME = 'header-search';

/** Shared drop-in for the search overlay list and All Categories menu. */
export const HEADER_SEARCH_DROPDOWN_MOTION_SX = {
  '@keyframes milletHeaderSearchDropdown': {
    from: {
      opacity: 0,
      transform: 'translateY(-4px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  animation: 'milletHeaderSearchDropdown 140ms ease-out',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
};

/** All Categories control — embedded in the search pill. */
export const HeaderAllCategoriesRoot = styled.div`
  position: relative;
  flex: 0 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  align-self: stretch;
  z-index: 3;
`;

export const HeaderAllCategoriesTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  padding: 0 10px;
  min-width: 0;
  max-width: 100%;
  height: 100%;
  border: none;
  border-radius: 0;
  background: transparent;
  color: ${(props) => props.theme.palette.text.primary};
  font: inherit;
  font-size: var(--font-body-sm);
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  appearance: none;
  outline: none;
  transition: color 0.16s ease, padding 0.16s ease, gap 0.16s ease;

  .header-all-categories-label-full,
  .header-all-categories-label-short {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .header-all-categories-label-short {
    display: none;
  }

  .header-all-categories-chevron {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    color: ${(props) => props.theme.palette.text.secondary};
    transition: transform 0.18s ease;
    transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  }

  &:hover {
    color: ${(props) => props.theme.palette.text.primary};

    .header-all-categories-chevron {
      color: ${(props) => props.theme.palette.text.primary};
    }
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.palette.accent.main};
    outline-offset: -2px;
    border-radius: 8px;
  }

  @container ${HEADER_SEARCH_CONTAINER_NAME} (max-width: 560px) {
    padding: 0 6px;
    gap: 2px;

    .header-all-categories-label-full {
      display: none;
    }

    .header-all-categories-label-short {
      display: inline;
    }

    .header-all-categories-chevron {
      width: 14px;
      height: 14px;
    }
  }
`;

/** Flex slot for the global marketplace search pill. */
export const HeaderSearchSlot = styled.div`
  flex: 0 1 720px;
  width: 100%;
  min-width: 0;
  max-width: 720px;
  margin: 0 16px;
  /* Allow suggestion dropdown to paint below the fixed header */
  overflow: visible;

  @media (max-width: 1000px) {
    margin: 0 12px;
  }

  @media (max-width: 900px) {
    margin: 0 10px;
  }
`;

// Auth Buttons
export const AuthButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  flex: 1 1 0;
  z-index: 2;
`;

export const ProfileActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  position: relative;
  line-height: 0;
`;

export const HeaderActionsDivider = styled.div`
  width: 1px;
  height: 24px;
  margin: 0 10px;
  background-color: ${(props) => props.theme.palette.border?.subtle};
  flex-shrink: 0;
  align-self: center;
`;

/** Icon-only row — keeps chat/profile centered without the dropdown affecting flex height. */
export const ProfileActionsCluster = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  height: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  line-height: 0;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex-shrink: 0;
`;

export const HeaderIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  height: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  padding: 0;
  border-radius: 10px;
  border: none;
  color: ${(props) => props.theme.palette.accent.main};
  background: transparent;
  cursor: pointer;
  outline: none;
  flex-shrink: 0;
  ${headerActionInteractive}

  .header-action-btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;

    svg {
      width: 20px;
      height: 20px;
      stroke: currentColor;
      fill: none;
    }
  }

`;

export const LoginLink = styled(HeaderGuestLink)``;

// Profile — same outer box as the navbar avatar; ring via box-shadow so the image stays centered
export const ProfileIcon = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  height: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  padding: 0;
  border: none;
  border-radius: 50%;
  box-sizing: border-box;
  appearance: none;
  background: transparent;
  cursor: pointer;
  color: ${(props) => props.theme.palette.text.primary};
  font: inherit;
  line-height: 0;
  outline: none;
  overflow: hidden;
  flex-shrink: 0;
  align-self: center;
  box-shadow: 0 0 0 2px transparent;
  transition: background-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;

  &:hover {
    box-shadow: 0 0 0 2px ${(props) => props.theme.palette.accent.main};
    background-color: ${(props) => alpha(props.theme.palette.accent.main, 0.2)};
  }

  &:active {
    background-color: ${(props) => alpha(props.theme.palette.accent.main, 0.28)};
    transform: scale(0.96);
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.palette.accent.main};
    outline-offset: 2px;
  }

  .profile-icon-svg {
    width: ${SITE_CHROME_CONTROL_SIZE_PX}px;
    height: ${SITE_CHROME_CONTROL_SIZE_PX}px;
    fill: ${(props) => props.theme.palette.text.primary};
    cursor: pointer;
    display: block;
  }

  .profile-picture {
    width: ${SITE_CHROME_CONTROL_SIZE_PX}px;
    height: ${SITE_CHROME_CONTROL_SIZE_PX}px;
    border-radius: 50%;
    object-fit: cover;
    object-position: center;
    cursor: pointer;
    border: none;
    display: block;
    flex-shrink: 0;
  }
`;

export const ProfileDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background-color: ${(props) => props.theme.palette.background.paper};
  border-radius: 12px;
  box-shadow:
    0 1px 2px ${(props) => alpha(props.theme.palette.text.primary, 0.04)},
    0 8px 20px ${(props) => alpha(props.theme.palette.text.primary, 0.12)};
  padding: 8px 0;
  min-width: 240px;
  z-index: 10011;
  border: 1px solid ${(props) => props.theme.palette.border?.subtle};
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.95)')};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: top right;
  overflow: hidden;
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};

  ul {
    ${listReset}

    li {
      padding: 8px 12px;
      margin: 2px 8px;
      border-radius: 8px;
      transition: background-color 0.2s ease, color 0.2s ease;

      &:hover {
        background-color: ${(props) => props.theme.palette.background.softHover};
      }

      &.active {
        background-color: ${(props) => props.theme.palette.background.soft};
      }

      &.active:hover {
        background-color: ${(props) => props.theme.palette.background.softHover};
      }

      &:first-of-type { margin-top: 4px; }
      &:last-child { margin-bottom: 4px; }

      a {
        color: ${(props) => props.theme.palette.text.primary};
        text-decoration: none;
        display: flex;
        align-items: center;
        width: 100%;
        font-size: var(--font-body-sm);
        font-weight: 500;
        font-family: ${fontFamily};
        cursor: pointer;
        transition: color 0.2s ease;

        .dropdown-icon {
          margin-right: 10px;
          width: 20px;
          height: 20px;
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: ${(props) => props.theme.palette.text.primary};
          transition: color 0.2s ease, opacity 0.15s ease;

          svg {
            width: 18px;
            height: 18px;
            stroke-width: 1.75px;
          }
        }
      }

      &.active a,
      &.active .dropdown-icon {
        color: ${(props) => props.theme.palette.accent.main};
      }

      &.active .dropdown-icon {
        opacity: 1;
      }
    }
  }
`;

// Notification Button
export const NotificationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  height: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  min-width: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  max-width: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  flex: 0 0 auto;
  overflow: visible;
  container-type: size;
  border-radius: 10px;
  border: none;
  box-sizing: border-box;
  color: ${(props) => props.theme.palette.accent.main};
  background: transparent;
  cursor: pointer;
  position: relative;
  padding: 0;
  outline: none;
  flex-shrink: 0;
  line-height: 0;
  ${headerActionInteractive}

  svg {
    width: 20px;
    height: 20px;
    display: block;
    flex-shrink: 0;
  }

  svg path {
    stroke: currentColor;
  }

  .notification-badge {
    position: absolute;
    top: 4cqh;
    right: 4cqw;
    z-index: 1;
    background-color: ${(props) => props.theme.palette.accent.main};
    color: ${(props) => props.theme.palette.primary.contrastText};
    border-radius: 999px;
    min-width: max(14px, 38cqmin);
    height: max(14px, 38cqmin);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: max(9px, 22cqmin);
    font-weight: 600;
    padding: 0 max(2px, 8cqmin);
    border: max(1px, 4.5cqmin) solid ${(props) => props.theme.palette.background.default};
    box-shadow: 0 2px 4px ${(props) => alpha(props.theme.palette.text.primary, 0.12)};
  }
`;

export { TooltipWrapper, Tooltip } from './tooltipStyles';
